import React, { useState, useEffect, useContext } from 'react';
import { fetchAllMissions, fetchCities } from '../utils/api';
import { UserDataContext } from '../context/UserContext';
import './ListView.css';

function MissionListView({ onLogout, onNavigateToQcMissions, onNavigateToActiveDuties }) {
  const { user } = useContext(UserDataContext);
  const username = user?.name || '';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(50);

  // City filter state
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const loadCities = async () => {
      setLoadingCities(true);
      const data = await fetchCities();
      if (data?.data && Array.isArray(data.data)) {
        setCities(data.data);
      } else if (Array.isArray(data)) {
        setCities(data);
      } else {
        setCities([]);
      }
      setLoadingCities(false);
    };
    loadCities();
  }, []);

  useEffect(() => {
    setCurrentPage(0); // Reset to first page when filters change
  }, [startDate, endDate, selectedCity]);

  useEffect(() => {
    loadMissions();
  }, [startDate, endDate, currentPage, selectedCity]);


  const loadMissions = async () => {
    try {
      setLoading(true);
      setError('');
      // Build query parameters
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCity) params.city = selectedCity;

      const response = await fetchAllMissions(params);

      // Handle different response structures
      const missions = response.data || response.missions || response || [];
      setItems(Array.isArray(missions) ? missions : []);

      // Set total count if available in response or estimate
      if (response.total !== undefined) {
        setTotalItems(response.total);
      } else if (response.count !== undefined) {
        setTotalItems(response.count);
      } else {
        // Estimate: if we got a full page, there might be more
        setTotalItems(Array.isArray(missions) && missions.length === pageSize
          ? (currentPage + 2) * pageSize
          : (currentPage * pageSize) + (Array.isArray(missions) ? missions.length : 0));
      }

    } catch (err) {
      setError(err.message || 'Failed to load missions. Please try again.');
      console.error('Error loading missions:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const title = (item.title || item.name || item.missionName || '').toLowerCase();
    const description = (item.description || item.details || '').toLowerCase();

    return title.includes(searchLower) || description.includes(searchLower);
  });

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  return (
    <div className="list-view-container">
      <header className="list-header">
        <div className="header-content">
          <h1 className="list-title">Mission View</h1>
          <div className="user-info">
            {onNavigateToActiveDuties && (
              <button
                onClick={onNavigateToActiveDuties}
                className="nav-button"
              >
                Active Duties
              </button>
            )}
            {onNavigateToQcMissions && (
              <button
                onClick={onNavigateToQcMissions}
                className="nav-button"
              >
                QC Missions
              </button>
            )}
            <span className="welcome-text">Welcome, {username}!</span>
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="list-content">
        <div className="filters-container">
          {/*<div className="search-bar">
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div> */}

          <div className="date-filters">
            {cities.length > 0 && (
              <div className="date-filter-group">
                <label htmlFor="city-select" className="date-label">City:</label>
                <select
                  id="city-select"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="date-input"
                  disabled={loadingCities}
                >
                  <option value="">All Cities</option>
                  {cities.map((city, index) => (
                    <option key={index} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="date-filter-group">
              <label htmlFor="start-date" className="date-label">Start Date:</label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            {endDate && (
              <div className="date-filter-group">
                <label htmlFor="end-date" className="date-label">End Date:</label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
            )}
            {(startDate || endDate || selectedCity) && (
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSelectedCity('');
                }}
                className="clear-date-button"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <div className="list-stats">
          <span className="stat-item">Total Missions: {items.length}</span>
          <span className="stat-item">Showing: {filteredItems.length}</span>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={loadMissions} className="retry-button">
              Retry
            </button>
          </div>
        )}

        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <p>Loading missions...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state">
              <p>{searchTerm ? 'No missions found matching your search.' : 'No missions available.'}</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Mission ID Planned</th>
                  <th>Duty ID</th>
                  <th>DSID</th>
                  <th>Collector Name</th>
                  <th>City</th>
                  <th>POI Count Reality</th>
                  <th>POIs Visible</th>
                  <th>POIs Not Visible</th>
                  <th>POI Added</th>
                  <th>Status</th>
                  <th>Start Time</th>
                  <th>Stop Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (

                  <tr key={item.id || item._id} className="table-row">
                    <td>{item.missionId || '-'}</td>
                    <td>{item.mission_duty?.dutyId || '-'}</td>
                    <td>{item.mission_duty?.collector?.dsid || '-'}</td>
                    <td>{item.mission_duty?.collector?.name || '-'}</td>
                    <td>{item.mission_duty?.collector?.city || '-'}</td>
                    <td>{item.valid + item.invalid + item.created || '0'}</td>
                    <td>{item.valid || '0'}</td>
                    <td>{item.invalid || '0'}</td>
                    <td>{item.created || '0'}</td>
                    <td>
                      <span className={`status-badge status-${item.status?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}>
                        {item.status || '-'}
                      </span>
                    </td>
                    <td>{item.mission_duty?.start_time ? new Date(item.mission_duty.start_time).toLocaleString() : '-'}</td>
                    <td>{item.mission_duty?.stop_time ? new Date(item.mission_duty.stop_time).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls */}
        {!loading && filteredItems.length > 0 && (
          <div className="pagination-container">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="pagination-button"
            >
              Previous
            </button>

            <span className="pagination-info">
              Page {currentPage + 1} of {Math.max(1, Math.ceil(totalItems / pageSize))}
            </span>

            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={items.length < pageSize}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div >
  );
}

export default MissionListView;
